import React, { useState } from 'react'
import LazyLoad from 'react-lazyload'
import classnames from 'classnames'
import { useMediaQuery } from '@helpers/hooks'
import { getLink } from '@helpers/env'
import {
  SizingWidgetBlock,
  SizingWidgetData
} from '@helpers/products/sizingWidgetData'
import { BREAKPOINT_SIZES } from '@consts/index'
import SectionHeader from '@components/ui/SectionHeader'
import SizingItem from './SizingItem'
import SizingItemMobile from './SizingItemMobileCard'
import styles from './styles.module.scss'
import ButtonLink, { ButtonLinkProps } from '@components/ui/Button/ButtonLink'
import { EmptyObject } from 'types/helpers'

type SizingWidgetButtonProps = {
  linkUrl: string
  linkText: string
  onLinkClick?: ButtonLinkProps['onClick']
}

function SizingWidgetButton({
  linkUrl,
  linkText,
  onLinkClick = () => undefined
}: SizingWidgetButtonProps) {
  return (
    <ButtonLink
      onClick={onLinkClick}
      href={linkUrl}
      variant="secondary"
      fullWidth="inherit"
      size="small"
      classes={styles.SizingWidget__button}
    >
      {linkText}
    </ButtonLink>
  )
}

type DesktopSizingWidgetProps = {
  selectedSizingBlock: SizingWidgetBlock
}

function DesktopSizingWidget({
  selectedSizingBlock
}: DesktopSizingWidgetProps) {
  return (
    <>
      {selectedSizingBlock.sizingItems.map(item => (
        <SizingItem key={item.title} item={item} />
      ))}
    </>
  )
}

function getSizingBlockForCategory(
  productCategory: string,
  sizingBlocks: SizingWidgetData['sizingBlocks']
) {
  const sizingBlockList = Object.values(sizingBlocks)
  return sizingBlockList.find(
    block => block.productType.toLowerCase() === productCategory.toLowerCase()
  )
}

type SizingWidgetProps = {
  productType?: string
  sizingWidgetData: SizingWidgetData | EmptyObject
  productCategory?: string
  sectionClasses?: string
}

function SizingWidget({
  sizingWidgetData,
  productType,
  productCategory,
  sectionClasses
}: SizingWidgetProps) {
  const [selectedMobileItem, setSelectedMobileItem] = useState({
    selected: false,
    selectedItem: 0
  })
  const { selected, selectedItem } = selectedMobileItem
  const { title, byline, linkText, linkUrl, sizingBlocks } =
    sizingWidgetData || {}
  const isMobileView = useMediaQuery({ maxWidth: BREAKPOINT_SIZES.TABLET - 1 })

  const containerStyles = classnames(styles.SizingWidget, {
    [styles.SizingWidget__mobile__wrapper]: isMobileView,
    [styles.SizingWidget__mobile__wrapper_product]:
      isMobileView && !!productCategory
  })
  const sizingWidgetWrapperStyles = classnames(styles.SizingWidget__wrapper, {
    [styles.SizingWidget__mobile]: isMobileView
  })
  const onClickMobileOpen = (i: number) => {
    setSelectedMobileItem({
      selected: true,
      selectedItem: i
    })
  }

  const onClickMobileClose = () => {
    setSelectedMobileItem({
      selected: false,
      selectedItem
    })
  }

  const getSelectedItemStyle = () => {
    return selected
      ? selectedItem === 0
        ? 'waist-moves-up'
        : 'waist-and-build-moves-up'
      : ''
  }

  if (!sizingBlocks) return null

  const matchingSizingBlock = productCategory
    ? getSizingBlockForCategory(productCategory, sizingBlocks)
    : sizingBlocks[0]

  if (!matchingSizingBlock) return null

  const excludedTypes = Object.values(
    matchingSizingBlock.excludeProductTypes || {}
  ).map(type => type.toLowerCase())

  if (productType && excludedTypes.includes(productType.toLowerCase())) {
    return null
  }

  const sizingItems = Object.values(matchingSizingBlock.sizingItems)

  const transformedSizingBlock = {
    ...matchingSizingBlock,
    sizingItems: sizingItems,
    renderedComponents: sizingItems.map(item => (
      <SizingItem
        key={item.title}
        item={item}
        mobile={isMobileView}
        active={selected}
        onClick={onClickMobileClose}
      />
    ))
  }

  return (
    <div className={containerStyles}>
      <div className={styles.SizingWidget__container}>
        {title && (
          <SectionHeader
            title={title}
            variant="left_aligned"
            className="reversed-mobile"
          />
        )}
        <div className={sizingWidgetWrapperStyles}>
          {isMobileView ? (
            <>
              {transformedSizingBlock.sizingItems.map((item, i) => (
                <SizingItemMobile
                  item={item}
                  key={item.title}
                  order={i}
                  moveStyle={getSelectedItemStyle()}
                  onClick={() => onClickMobileOpen(i)}
                />
              ))}
              {transformedSizingBlock.renderedComponents[selectedItem]}
            </>
          ) : (
            <DesktopSizingWidget selectedSizingBlock={transformedSizingBlock} />
          )}
        </div>
        {linkUrl && linkText && (
          <SizingWidgetButton linkUrl={getLink(linkUrl)} linkText={linkText} />
        )}
      </div>
    </div>
  )
}

export default SizingWidget
